import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../api/axiosConfig';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Async thunks
export const fetchAgents = createAsyncThunk(
  'agents/fetchAgents',
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/admin/agents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { data: response.data, timestamp: Date.now() };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch agents');
    }
  }
);

export const createAgent = createAsyncThunk(
  'agents/createAgent',
  async ({ agentData, token }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/admin/agents', agentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create agent');
    }
  }
);

export const updateAgent = createAsyncThunk(
  'agents/updateAgent',
  async ({ email, agentData, token }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/api/admin/agents/${encodeURIComponent(email)}`, agentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update agent');
    }
  }
);

export const deleteAgent = createAsyncThunk(
  'agents/deleteAgent',
  async ({ email, token }, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/admin/agents/${email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return email;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete agent');
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  lastFetched: null
};

const agentSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch agents
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data;
        state.lastFetched = action.payload.timestamp;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create agent
      .addCase(createAgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update agent
      .addCase(updateAgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(a => a.email === action.payload.email);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete agent
      .addCase(deleteAgent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(a => a.email !== action.payload);
      })
      .addCase(deleteAgent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Selectors
export const selectAgents = (state) => state.agents.list;
export const selectAgentsLoading = (state) => state.agents.loading;
export const selectAgentsError = (state) => state.agents.error;
export const selectShouldRefetch = (state) => {
  if (!state.agents.lastFetched) return true;
  return Date.now() - state.agents.lastFetched > CACHE_DURATION;
};

export const { clearError } = agentSlice.actions;
export default agentSlice.reducer;
